'use client'

import React, { useCallback, useEffect, useState, useRef } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { Play } from 'lucide-react'
import { cn } from 'lib/utils'
import { Product } from 'lib/types'
import ImageWithFoster from 'components/ImageWithFoster'
import Link from 'next/link'
import { HomePrevButton } from 'components/Button/HomePrevButton'
import { HomeNextButton } from 'components/Button/HomeNextButton'

interface ProductCardProps {
  product: Product
  isCenter?: boolean
  index: number
}

import styles from './index.module.scss'

const ProductCard: React.FC<ProductCardProps> = ({ product, isCenter, index }) => {
  return (
    <div className={`${styles.bgColor} relative h-full overflow-hidden transition-all duration-300 z-10`}>
      <div
        className={`${
          isCenter ? 'absolute' : 'hidden'
        } overflow-hidden w-[50px] sm:w-[30px] h-[50px] sm:h-[30px] top-4 left-4 z-50`}
      >
        <ImageWithFoster src={product.avatar} alt={product.name} width={100} height={100} className="rounded-full" />
      </div>

      <div className="overflow-hidden w-full w-[300px] h-[400px]">
        <ImageWithFoster
          src={product.image}
          alt={product.name}
          className={`${isCenter ? 'object-contain' : 'w-full h-full max-w-full max-h-full object-cover'}`}
        />
      </div>

      {isCenter && (
        <div className={styles.bottomSection}>
          <div>
            <div className={styles.name}>{product.name}</div>
            <div className={styles.price}>${product.price}</div>
          </div>
        </div>
      )}
      {/* <div
        className={`absolute inset-0 z-100 ${
          isCenter
            ? 'bg-black/0'
            : index === 1 || index === 7
            ? 'bg-black/10'
            : index === 2 || index === 6
            ? 'bg-black/20'
            : index === 3 || index === 5
            ? 'bg-black/40'
            : 'bg-black/40'
        }`}
      ></div> */}
    </div>
  )
}

interface DiscoveryFeedProps {
  products: Product[]
}

export const DiscoveryFeed: React.FC<DiscoveryFeedProps> = ({ products }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    loop: true,
    dragFree: false,
    startIndex: 0,
  })

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [progress, setProgress] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    const selectedSnap = emblaApi.selectedScrollSnap()
    setSelectedIndex(selectedSnap)
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  // Auto-play functionality with progress tracking
  useEffect(() => {
    if (!emblaApi || !isPlaying) {
      setProgress(0)
      return
    }

    const duration = 4000 // 4 seconds
    const startTime = Date.now()

    const updateProgress = () => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.min((elapsed / duration) * 100, 100)
      setProgress(newProgress)

      if (newProgress >= 100) {
        emblaApi.scrollNext()
        setProgress(0)
      }
    }

    const interval = setInterval(updateProgress, 16) // ~60fps

    return () => clearInterval(interval)
  }, [emblaApi, isPlaying, selectedIndex])

  const toggleAutoPlay = () => {
    setIsPlaying(!isPlaying)
    setProgress(0) // Reset progress when toggling
  }

  const handleManualNavigation = (direction: 'prev' | 'next') => {
    setProgress(0) // Reset progress on manual navigation
    if (direction === 'prev') {
      scrollPrev()
    } else {
      scrollNext()
    }
  }

  // Monitor visibility and pause when invisible
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      entries => {
        const isVisible = entries[0]?.isIntersecting
        if (isVisible) {
          setIsPlaying(true)
        } else {
          setIsPlaying(false)
        }
      },
      {
        threshold: 0.1, // Consider invisible if less than 10% is visible
      }
    )

    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="flex items-center justify-center w-full h-full relative"
      style={{ userSelect: 'none' }}
    >
      {/* Main Carousel */}
      <div className="w-full overflow-hidden py-[72px] sm:py-32 lg:py-[160px] 2xl:py-[160px]" ref={emblaRef}>
        <div className="flex">
          {products.map((product, index) => {
            const isCenter = index === selectedIndex

            // Calculate z-index: center is highest, then decreases as distance increases
            const count = products.length
            let distanceFromCenter = Math.abs(index - selectedIndex)

            // Handle circular distance for proper z-index calculation
            const circularDistance = Math.min(distanceFromCenter, count - distanceFromCenter)

            // Calculate z-index: center gets highest value, decreases with distance
            const zIndex = isCenter ? 50 : Math.max(50 - circularDistance * 5, 1)

            return (
              <div
                key={`${product.id}-${index}`}
                className={`flex-[0_0_300px] sm:flex-[0_0_300px] min-w-0 mx-0`}
                style={{
                  zIndex,
                }}
              >
                <div
                  className={cn(
                    'relative group transition-all duration-500 ease-out transform origin-center',
                    isCenter ? 'scale-[1.18] sm:scale-[1.5]' : 'scale-[1.08] sm:scale-[1.3]'
                  )}
                >
                  <div
                    className={`absolute -inset-x-[-5px] ${
                      !isCenter ? 'inset-y-[25px]' : 'inset-y-[45px]'
                    } bg-black/50 rounded-md z-0`}
                    style={{ boxShadow: '0px 0px 15px 15px rgba(0, 0, 0, 0.6)' }}
                  />
                  <Link href={product.link} passHref>
                    <a>
                      <ProductCard product={product} isCenter={isCenter} index={Math.abs(index - selectedIndex)} />
                    </a>
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="hidden sm:flex absolute justify-center sm:right-6 bottom-24 gap-2">
        {/* Play/Pause Button with Circular Progress */}
        <div className="relative" onClick={toggleAutoPlay}>
          {/* Circular Progress Background */}
          <svg className="absolute inset-0 w-12 h-12 -rotate-90 z-20" viewBox="0 0 48 48">
            <circle cx="24" cy="24" r="22" stroke="rgba(255, 255, 255, 0.2)" strokeWidth="4" fill="none" />
            <circle
              cx="24"
              cy="24"
              r="22"
              stroke="white"
              strokeWidth="4"
              fill="none"
              strokeDasharray={`${2 * Math.PI * 22}`}
              strokeDashoffset={`${2 * Math.PI * 22 * (1 - progress / 100)}`}
              className="transition-all duration-75 ease-linear"
            />
          </svg>

          <button className="relative flex items-center justify-center h-12 w-12 rounded-full bg-black/80 backdrop-blur-sm border-white/20 text-white hover:bg-black/90 hover:scale-110 transition-all duration-300 z-10">
            {isPlaying ? (
              <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="5" height="20" fill="white" />
                <rect x="11" width="5" height="20" fill="white" />
              </svg>
            ) : (
              <Play className="h-5 w-5" />
            )}
          </button>
        </div>

        <HomePrevButton onClick={() => handleManualNavigation('prev')} />

        <HomeNextButton onClick={() => handleManualNavigation('next')} />
      </div>
    </div>
  )
}
